/**
 * <StagingBanner> — "you are NOT looking at the real application".
 *
 * QC opens a fix's staging copy in the ordinary Run view; the only thing that
 * tells it apart from production is this strip. So it is loud, it names the
 * issue, and it says the data is sample data — a tester must never mistake a
 * staging record for a customer's, or the reverse.
 *
 * Renders nothing unless the environment is `staging`. The host passes the
 * environment it resolved, or the runtime + requested env and the banner asks.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { FlaskConical, LogOut } from 'lucide-react'

import type { SupportEnvironment, SupportRuntime } from './contract'

export interface StagingBannerProps {
  /** The resolved environment; when absent it is fetched with `runtime` + `env`. */
  environment?: SupportEnvironment | null
  runtime?: SupportRuntime
  /** The requested environment (`stage:<n>`). */
  env?: string | null
  /** Leave staging (the host drops the env and reloads). */
  onExit?: () => void
}

export default function StagingBanner({ environment, runtime, env, onExit }: StagingBannerProps) {
  const [fetched, setFetched] = useState<SupportEnvironment | null>(null)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (environment || !runtime?.environment || !env) return undefined
    let cancelled = false
    runtime.environment(env)
      .then((e) => { if (!cancelled) { setFetched(e); setFailed(false) } })
      .catch(() => { if (!cancelled) setFailed(true) })
    return () => { cancelled = true }
  }, [environment, runtime, env])

  const e = environment || fetched
  // Asked for staging but the server would not confirm it: still say so —
  // silence here would let a tester believe they are on staging when the
  // request may well be running against production.
  if (failed && env) {
    return (
      <Strip tone="#b45309" onExit={onExit}>
        Requested {env.replace(':', ' #')} but the environment could not be confirmed — treat this as LIVE data.
      </Strip>
    )
  }
  if (!e || e.kind !== 'staging') return null
  return (
    <Strip tone="#c2410c" onExit={onExit}>
      STAGING{e.issue_number ? ` — issue #${e.issue_number}` : ''} — sample data
      {e.label ? <span style={{ fontWeight: 500, opacity: 0.9 }}> · {e.label}</span> : null}
    </Strip>
  )
}

function Strip({ tone, children, onExit }: { tone: string; children: ReactNode; onExit?: () => void }) {
  return (
    <div role="status" style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
      padding: '0.35rem 0.9rem', background: tone, color: '#fff',
      fontWeight: 700, fontSize: '0.8625rem', letterSpacing: '0.02em', flexShrink: 0,
      backgroundImage: 'repeating-linear-gradient(135deg, rgba(255,255,255,0.08) 0 12px, transparent 12px 24px)',
    }}>
      <FlaskConical size={14} />
      <span>{children}</span>
      {onExit && (
        <button type="button" onClick={onExit} style={{
          display: 'inline-flex', alignItems: 'center', gap: 4, marginLeft: 8,
          padding: '0.15rem 0.55rem', borderRadius: 999, cursor: 'pointer',
          border: '1px solid rgba(255,255,255,0.7)', background: 'transparent', color: '#fff',
          fontSize: '0.8125rem', fontWeight: 700,
        }}>
          <LogOut size={11} /> Leave staging
        </button>
      )}
    </div>
  )
}
